/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from '@reach/router';
import classnames from 'classnames';
import Picture from 'components/Picture/Picture';
import useWindowWidth from 'effects/useWindowWidth';
import useEnhanced from 'effects/enhanced';
import Nav from '../Nav/Nav';
import useAnimation from './animation';
import './Index.scss';

const titleSizes = [
  'default',
  'xlarge',
  'large',
  'medium',
  'small',
  'tiny',
  'midget',
];

/**
 * This is the Index component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Index({
  title,
  layout,
  theme,
  picture,
  labels,
  nav,
  sponsors,
  onMount,
  onTheme,
  onNavigate,
}) {
  const [ref] = useAnimation();
  const titleRef = useRef(null);
  const enhanced = useEnhanced(890);
  const windowWidth = useWindowWidth();
  const [titleSize, setTitleSize] = useState(null);

  useEffect(() => {
    onMount();
  }, []);

  useEffect(() => {
    document.body.classList.add(layout);

    const timeout = setTimeout(() => {
      onTheme(theme);
    }, 120);

    return () => {
      clearTimeout(timeout);
      document.body.classList.remove(layout);
    };
  }, [layout]);

  useEffect(() => {
    setTitleSize(null);
  }, [windowWidth]);

  useEffect(() => {
    const current = titleSize ?? 0;
    if (titleRef.current) {
      if (titleSizes[current + 1]) {
        if (
          titleRef.current.offsetWidth >
            titleRef.current.parentNode.offsetWidth ||
          (enhanced &&
            titleRef.current.offsetHeight > ref.current.offsetHeight * 0.45) ||
          (!enhanced &&
            titleRef.current.offsetHeight > ref.current.offsetHeight * 0.2)
        ) {
          setTitleSize(size => (size ?? 0) + 1);
        } else if (titleSize === null) {
          setTitleSize(0);
        }
      }
    }
  }, [titleSize]);

  function renderNav() {
    if (enhanced) {
      return (
        <nav className="menu">
          {nav.map(({ id, text, url }) => {
            if (url) {
              return (
                <Link
                  key={id}
                  to={url}
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              );
            }

            return <span key={id}>{text}</span>;
          })}
        </nav>
      );
    }

    return <Nav items={nav} labels={labels} onClick={onNavigate} />;
  }

  function renderLogo({ src, width, height }) {
    let renderWidth = width;
    let renderHeight = Math.round((height / width) * renderWidth);

    if (renderHeight > 40) {
      renderHeight = 40;
      renderWidth = Math.round((width / height) * renderHeight);
    }

    return (
      <div
        className="svg"
        dangerouslySetInnerHTML={{ __html: src }}
        style={{ width: renderWidth, aspectRatio: `${width} / ${height}` }}
      />
    );
  }

  function renderSponsors() {
    if (!sponsors.length) return null;

    return (
      <>
        <div className="sponsor-background" />
        <div className="sponsors">
          {sponsors.map(({ id, title, url, logo, ...props }) => (
            <a
              key={id}
              href={url}
              title={title}
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {renderLogo(logo)}
            </a>
          ))}
        </div>
      </>
    );
  }

  return (
    <main ref={ref} className="page index">
      {renderNav()}
      <strong className="name">
        <span>{labels.name}</span>
      </strong>
      <h1>
        <span
          ref={titleRef}
          className={classnames(
            'lined-text lined-title',
            titleSizes[titleSize] ?? null
          )}
          dangerouslySetInnerHTML={{ __html: title }}
        />
      </h1>
      <div className="image">
        <Picture {...picture} />
      </div>
      {renderSponsors()}
    </main>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Index.propTypes = {
  title: PropTypes.string.isRequired,
  layout: PropTypes.oneOf(['small-image', 'large-image']),
  theme: PropTypes.string.isRequired,
  picture: PropTypes.object,
  labels: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired,
  nav: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      text: PropTypes.string.isRequired,
      url: PropTypes.string,
    })
  ).isRequired,
  sponsors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      logo: PropTypes.shape({
        src: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
      }).isRequired,
      url: PropTypes.string,
    })
  ).isRequired,
  onMount: PropTypes.func.isRequired,
  onTheme: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Index.defaultProps = {
  layout: 'small-image',
  theme: 'white',
  nav: [],
  sponsors: [],
  labels: {
    name: 'Name',
    menu: 'Menu',
    open: 'Open',
    close: 'Close',
  },
  onMount: () => {},
  onTheme: () => {},
  onNavigate: () => {},
};

export default Index;
